import PropTypes from 'prop-types';
import exact from 'prop-types-exact';
import qs from 'querystringify';
import React, { Component } from 'react';

import jsonGet from '../../../util/jsonGet';
import jsonPost from '../../../util/jsonPost';
import { inspireMeDropdownValue } from './constants';

import Button from '../Atoms/Button';
import Dropdown from '../Atoms/Dropdown';
import ErrorBoundary from '../Core/ErrorBoundary';
import Loading from '../Core/Loading';
import FeatureCarousel from '../FeatureCarousel';
import Filter from './Filter';
import Inclusion from './Inclusion';
import InspireMe from './InspireMe';
import Results from './Results';
import TournamentFilter from './TournamentFilter';
import PackageFilterContainer from './modules/PackageFilterContainer';
import { getInitialCurrencyState } from '../../../util/currency';
import { addCultureAndFallbackToParamsFromLocalStorage } from '../../../util/culture';
import getDefaultFilterOptionsForSelectedCurrency from './helpers';
import { classifyFilterChange, getPriceRangeFromSearchResults } from './priceFilter';
import URL_PARAMS from '../../../util/urlParams';
import CUSTOM_EVENT from '../../../util/customEvents';

const filterItemPropTypes = PropTypes.shape({
  id: PropTypes.string,
  title: PropTypes.string,
});

const inspireMeGroupPropTypes = PropTypes.shape({
  items: PropTypes.arrayOf(filterItemPropTypes),
});

const filtersPropTypes = PropTypes.shape({
  allOptionText: PropTypes.string,
  packageTypeSubFilterAllOptionText: PropTypes.string,
  enableMatchFilterAndOrSelection: PropTypes.bool,
  prices: PropTypes.arrayOf(
    PropTypes.shape({
      currency: PropTypes.string,
      minimumPriceValue: PropTypes.number,
      maximumPriceValue: PropTypes.number,
    })
  ),
  durations: PropTypes.arrayOf(filterItemPropTypes),
  accommodationTypes: PropTypes.arrayOf(filterItemPropTypes),
  inclusions: PropTypes.arrayOf(filterItemPropTypes),
  packageTypes: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      title: PropTypes.string,
      disableAccommodationFilter: PropTypes.bool,
      tourTypeSubFilter: PropTypes.arrayOf(filterItemPropTypes),
    })
  ),
  tournaments: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      title: PropTypes.string,
      matches: PropTypes.arrayOf(filterItemPropTypes),
      destinations: PropTypes.arrayOf(filterItemPropTypes),
    })
  ),
  inspireMe: PropTypes.shape({
    destinations: inspireMeGroupPropTypes,
    holidayTypes: inspireMeGroupPropTypes,
    attractions: inspireMeGroupPropTypes,
    partySizes: inspireMeGroupPropTypes,
  }),
});

const propTypes = exact({
  apiURL: PropTypes.string.isRequired,
  autoplay: PropTypes.bool.isRequired,
  crmApiURL: PropTypes.string.isRequired,
  defaultSortOrder: PropTypes.number.isRequired,
  featuredItems: PropTypes.arrayOf(PropTypes.object).isRequired,
  filters: filtersPropTypes.isRequired,
  fromPriceText: PropTypes.string,
  hideAccommodationTypeFilter: PropTypes.bool,
  hideDateRangeFilter: PropTypes.bool,
  hideDestinationFilter: PropTypes.bool,
  hideDurationFilter: PropTypes.bool,
  hideFilters: PropTypes.bool.isRequired,
  hideInspireMeFilterGroup: PropTypes.bool,
  hideInclusionFilter: PropTypes.bool,
  hideMatchFilter: PropTypes.bool,
  hidePriceRangeFilter: PropTypes.bool,
  hideSearchResults: PropTypes.bool.isRequired,
  hideTournamentFilter: PropTypes.bool,
  hideTourSubTypeFilter: PropTypes.bool,
  hideTourTypeFilter: PropTypes.bool,
  interval: PropTypes.number.isRequired,
  labels: PropTypes.object.isRequired,
  limitedAvailabilityText: PropTypes.string.isRequired,
  loadMoreButtonText: PropTypes.string,
  perPersonText: PropTypes.string,
  noResultsMessage: PropTypes.string.isRequired,
  resetButtonText: PropTypes.string,
  resultsPerPage: PropTypes.number.isRequired,
  searchResultsLayout: PropTypes.number.isRequired,
  siteKey: PropTypes.string.isRequired,
  soldOutText: PropTypes.string.isRequired,
  viewTourText: PropTypes.string,
  waitingListText: PropTypes.string,
  widenSearchNoResultsMessage: PropTypes.string,
});

const defaultProps = {
  fromPriceText: 'from',
  hideAccommodationTypeFilter: false,
  hideDateRangeFilter: false,
  hideDestinationFilter: false,
  hideDurationFilter: false,
  hideInspireMeFilterGroup: false,
  hideInclusionFilter: false,
  hideMatchFilter: false,
  hidePriceRangeFilter: false,
  hideTournamentFilter: false,
  hideTourSubTypeFilter: false,
  hideTourTypeFilter: false,
  loadMoreButtonText: 'Load More',
  resetButtonText: 'reset',
  viewTourText: 'view package',
  waitingListText: null,
  widenSearchNoResultsMessage: null,
  perPersonText: 'pp',
};

const searchResultsLayout = {
  List: 0,
  Grid: 1,
};

const tourSearchSortOrder = {
  none: 0,
  priceDescending: 1,
  priceAscending: 2,
  durationDescending: 3,
  durationAscending: 4,
};

const setScrollHistory = () => {
  try {
    const scrollTop = document.scrollingElement
      ? document.scrollingElement.scrollTop
      : document.documentElement.scrollTop;
    if (scrollTop) {
      window.localStorage.setItem('scrollState', scrollTop);
    }
  } catch (error) {
    console.error('Error saving scroll state', error);
  }
};

// Package types in scope for the current selection: just the selected type, or
// every configured type when viewing "All" (no package type selected).
const getRelevantPackageTypes = (packageTypes, selectedPackageTypeId) =>
  selectedPackageTypeId
    ? packageTypes?.filter((packageType) => packageType.id === selectedPackageTypeId)
    : packageTypes;

const parseMultiSelectQueryParam = (value) => {
  if (Array.isArray(value)) {
    return value;
  }

  if (typeof value === 'string' && value.length > 0) {
    return value.split(',');
  }

  return [];
};

class PackageSearch extends Component {
  constructor(props) {
    super(props);

    this.handlePaginationClick = this.handlePaginationClick.bind(this);
    this.handleReset = this.handleReset.bind(this);
    this.handleValueChange = this.handleValueChange.bind(this);
    this.handleTournamentValueChange = this.handleTournamentValueChange.bind(this);
    this.setQuery = this.setQuery.bind(this);

    // Tracks whether the price range currently held in state is the customer's
    // own slider selection. When true it is what gets searched on and the API
    // response will not override it. When false the search runs on the CMS
    // price boundary and the handles reflow to the results that come back.
    // Cleared by any non-price filter change, Reset, and a currency change.
    this.priceFilterSetByUser = false;

    this.tournamentFilterRef = React.createRef(); // we need to specifically instruct tournament filters to reset.

    this.sortOptions = [
      {
        text: props.labels.defaultSortOrderText
          ? `${props.labels.sortOrderLabelText}: ${props.labels.defaultSortOrderText}`
          : props.labels.sortOrderLabelText,
        value: tourSearchSortOrder.none,
      },
      {
        text: `${props.labels.sortOrderLabelText}: ${props.labels.priceAscendingSortOrderText}`,
        value: tourSearchSortOrder.priceAscending,
      },
      {
        text: `${props.labels.sortOrderLabelText}: ${props.labels.priceDescendingSortOrderText}`,
        value: tourSearchSortOrder.priceDescending,
      },
      {
        text: `${props.labels.sortOrderLabelText}: ${props.labels.durationAscendingSortOrderText}`,
        value: tourSearchSortOrder.durationAscending,
      },
      {
        text: `${props.labels.sortOrderLabelText}: ${props.labels.durationDescendingSortOrderText}`,
        value: tourSearchSortOrder.durationDescending,
      },
    ];
    const initialCurrency = getInitialCurrencyState(props.filters.prices);

    this.state = {
      filterOptions: getDefaultFilterOptionsForSelectedCurrency({
        currencyCode: initialCurrency.code,
        filters: props.filters,
        resultsPerPage: this.props.resultsPerPage,
        siteKey: this.props.siteKey,
        defaultSortOrder: this.props.defaultSortOrder,
      }),
      currentCurrency: getInitialCurrencyState(props.filters.prices),
      items: null,
      loadingPage: false,
      loadingResults: !props.hideSearchResults,
      tournamentID: null,
      packageTypes: '',
      tourTypeSubFilters: [],
      includeAllMatchFilterItems: false,
      totalItemCount: 0,
      initialScroll: 0,
      scrolled: false,
      lastPage: 1,
    };
  }

  componentDidMount() {
    if (this.props.hideSearchResults) return;

    this.handleQuery((filterOptions) => {
      this.setQuery(filterOptions);
    });
    sessionStorage.removeItem('selectedFlights');
    window.addEventListener('scroll', setScrollHistory, { passive: true });
    window.addEventListener(CUSTOM_EVENT.CURRENCY_CHANGE, this.handleCurrencyChange);
    window.addEventListener('popstate', this.handlePopState);
  }

  componentWillUnmount() {
    if (this.props.hideSearchResults) return;

    window.removeEventListener('scroll', setScrollHistory);
    window.removeEventListener(CUSTOM_EVENT.CURRENCY_CHANGE, this.handleCurrencyChange);
    window.removeEventListener('popstate', this.handlePopState);
  }

  setQuery(filterOptions, pushHistory = true) {
    if (this.props.hideSearchResults) return;
    const { minPrice, maxPrice, priceFrom, priceTo, ...queryParams } = filterOptions || {};
    const paramString = qs.stringify(
      {
        ...queryParams,
        // Only the customer's own price selection belongs in the URL. The range
        // shown after a search is derived from the results, and persisting that
        // would make it look hand-picked when the page is refreshed or reached
        // through browser back/forward.
        ...(this.priceFilterSetByUser && priceFrom !== undefined && priceTo !== undefined
          ? { [URL_PARAMS.PRICE_FROM]: priceFrom, [URL_PARAMS.PRICE_TO]: priceTo }
          : {}),
        [URL_PARAMS.CURRENCY]: this.state.currentCurrency.code,
      },
      true
    );
    const path = window.location.pathname + paramString;
    if (pushHistory) {
      window.history.pushState(null, null, path);
    } else {
      window.history.replaceState(null, null, path);
    }
  }

  handleCurrencyChange = (event) => {
    this.priceFilterSetByUser = false;
    const newCurrencyCode = event?.detail?.code; // user selected new currency
    // Determine which filter price in what currency to use dependent on the newly selected currency
    const newCurrencyFilterDataWithMinAndMaxValues = this.props.filters.prices.find(
      (price) => price.currency === newCurrencyCode
    );

    if (newCurrencyFilterDataWithMinAndMaxValues) {
      this.setState(
        (prevState) => ({
          currentCurrency: event.detail,
          filterOptions: {
            ...prevState.filterOptions,
            priceFrom: newCurrencyFilterDataWithMinAndMaxValues.minimumPriceValue,
            priceTo: newCurrencyFilterDataWithMinAndMaxValues.maximumPriceValue,
            minPrice: newCurrencyFilterDataWithMinAndMaxValues.minimumPriceValue,
            maxPrice: newCurrencyFilterDataWithMinAndMaxValues.maximumPriceValue,
          },
        }),
        () => {
          this.setQuery(this.state.filterOptions);
        }
      );
    } else {
      console.error('Currency data not found for:', newCurrencyCode);
    }
  };

  handleQuery(callback) {
    if (window.location.search) {
      const currentCurrencyCode = this.state.currentCurrency.code;
      const currentCurrencyData = this.props.filters.prices.find(
        (price) => price.currency === currentCurrencyCode
      );

      const parsedQuery = qs.parse(window.location.search);

      // A price in the URL is only ever written there by the customer's own
      // slider selection, so treat it as user-set and leave it alone. Without
      // one, the search runs on the CMS boundary again — this has to reset the
      // flag as well as set it, so browser back/forward to a state with no
      // price selection restores the boundary search.
      this.priceFilterSetByUser = Boolean(
        parsedQuery[URL_PARAMS.PRICE_FROM] || parsedQuery[URL_PARAMS.PRICE_TO]
      );

      const minPrice = currentCurrencyData.minimumPriceValue;
      const maxPrice = currentCurrencyData.maximumPriceValue;
      const parsedPriceFrom = Number(parsedQuery[URL_PARAMS.PRICE_FROM]) || minPrice;
      const parsedPriceTo = Number(parsedQuery[URL_PARAMS.PRICE_TO]) || maxPrice;

      // Ensure priceFrom and priceTo are within the valid range
      const priceFrom = Math.max(parsedPriceFrom, minPrice);
      const priceTo = Math.min(parsedPriceTo, maxPrice);

      this.setState(
        (prevState) => {
          const filterOptions = {
            ...prevState.filterOptions,
            ...parsedQuery,
            [URL_PARAMS.PAGE]: prevState.filterOptions.page || 1,
            [URL_PARAMS.RESULTS_PER_PAGE]:
              Number(parsedQuery[URL_PARAMS.RESULTS_PER_PAGE]) ||
              prevState.filterOptions.resultsPerPage,
            [URL_PARAMS.SITE_KEY]:
              parsedQuery[URL_PARAMS.SITE_KEY] || prevState.filterOptions.siteKey,
            [URL_PARAMS.TOURNAMENTS]:
              parseMultiSelectQueryParam(parsedQuery[URL_PARAMS.TOURNAMENTS]).length > 0
                ? parseMultiSelectQueryParam(parsedQuery[URL_PARAMS.TOURNAMENTS])
                : prevState.filterOptions.tournaments,
            [URL_PARAMS.DURATIONS]: parseMultiSelectQueryParam(parsedQuery[URL_PARAMS.DURATIONS]),
            [URL_PARAMS.ACCOMMODATION_TYPE_IDS]: parseMultiSelectQueryParam(
              parsedQuery[URL_PARAMS.ACCOMMODATION_TYPE_IDS]
            ),
            [URL_PARAMS.INCLUSIONS]: parseMultiSelectQueryParam(parsedQuery[URL_PARAMS.INCLUSIONS]),
            [URL_PARAMS.MATCHES]: parseMultiSelectQueryParam(parsedQuery[URL_PARAMS.MATCHES]),
            [URL_PARAMS.DESTINATIONS]: parseMultiSelectQueryParam(
              parsedQuery[URL_PARAMS.DESTINATIONS]
            ),
            [URL_PARAMS.PACKAGE_TYPES]: parsedQuery[URL_PARAMS.PACKAGE_TYPES] || '',
            [URL_PARAMS.SORT_ORDER]:
              Number(parsedQuery[URL_PARAMS.SORT_ORDER]) || prevState.filterOptions.sortOrder,
            priceFrom,
            priceTo,
            minPrice,
            maxPrice,
          };

          const initialScroll = Number(localStorage.scrollState) || 0;
          const lastPage = Number(parsedQuery[URL_PARAMS.PAGE]) || null;

          return {
            filterOptions,
            initialScroll,
            lastPage,
          };
        },
        () => {
          this.loadResults();
          if (callback) callback();
        }
      );
    } else {
      // No URL parameters; reset filters to default
      this.priceFilterSetByUser = false;
      this.setState(
        (prevState) => ({
          filterOptions: getDefaultFilterOptionsForSelectedCurrency({
            currencyCode: prevState.currentCurrency.code,
            filters: this.props.filters,
            resultsPerPage: this.props.resultsPerPage,
            siteKey: this.props.siteKey,
            defaultSortOrder: this.props.defaultSortOrder,
          }),
          loadingResults: true,
        }),
        () => {
          this.loadResults();
          if (callback) callback(this.state.filterOptions);
        }
      );
    }
  }

  handlePopState = () => {
    this.handleQuery(() => {
      this.loadResults(false, false);
    });
  };

  handlePaginationClick = () => {
    this.setState(
      (prevState) => ({
        filterOptions: {
          ...prevState.filterOptions,
          page: prevState.filterOptions.page + 1,
        },
        loadingPage: true,
      }),
      () => {
        this.loadResults(true);
      }
    );
  };

  handleValueChange = (payload) => {
    const { priceChanged, filterChanged } = classifyFilterChange(payload);

    if (priceChanged) {
      this.priceFilterSetByUser = true;
    } else if (filterChanged) {
      // Changing any other filter searches on the CMS price boundary again, so
      // the range reflows to the packages that now match instead of staying
      // pinned to the narrower range left by the previous search.
      this.priceFilterSetByUser = false;
    }

    this.setState(
      (prevState) => ({
        filterOptions: {
          ...prevState.filterOptions,
          ...payload,
          page: 1, // Reset page when filters change
        },
        loadingResults: true,
      }),
      () => {
        this.loadResults();
      }
    );
  };

  handleTournamentValueChange = (payload) => {
    const updatedPayload = { ...payload };
    if (updatedPayload.tournaments.length === 0) {
      // if we have no tournaments selected then we want to tell the api to pull in all.
      updatedPayload.tournaments = this.props.filters.tournaments.map((t) => t.id);
    }
    updatedPayload.matches = [];
    this.handleValueChange(updatedPayload);
  };

  handlePackageValueChange = (packageTypes, tourTypeSubFilters) => {
    this.handleValueChange({ packageTypes, tourTypeSubFilters });
  };

  handleMatchFilterAndOrChange = (e) => {
    this.priceFilterSetByUser = false;
    this.setState({ includeAllMatchFilterItems: e.target.checked }, () => {
      this.loadResults();
    });
  };

  handleReset() {
    this.priceFilterSetByUser = false;
    if (this.tournamentFilterRef.current) {
      this.tournamentFilterRef.current.reset();
    }
    this.setState(
      (prevState) => ({
        filterOptions: getDefaultFilterOptionsForSelectedCurrency({
          currencyCode: prevState.currentCurrency.code,
          filters: this.props.filters,
          resultsPerPage: this.props.resultsPerPage,
          siteKey: this.props.siteKey,
          defaultSortOrder: this.props.defaultSortOrder,
        }),
        loadingResults: true,
      }),
      () => {
        this.loadResults();
        this.setQuery(this.state.filterOptions);
      }
    );
  }

  // eslint-disable-next-line default-param-last
  loadResults = (concatItemSets = false, pushHistory = true) => {
    if (this.props.hideSearchResults) return;
    const filterOptions = { ...this.state.filterOptions };
    let tournamentID = null;

    // Store timestamp as API request ID
    const apiRequestID = Date.now();
    this.apiRequestID = apiRequestID;

    const { matches, accommodationTypeIds } = filterOptions;
    const { tournaments, inspireMe, accommodationTypes, packageTypes } = this.props.filters;

    // In the "All" view (no package type selected) every package type is in scope,
    // so if any of them disables the accommodation filter, it must stay excluded -
    // forcing it would AND against tours (eg. Flight & Match-day) that have no
    // accommodation option in the index and zero out the results.
    const relevantPackageTypes = getRelevantPackageTypes(packageTypes, filterOptions.packageTypes);
    const isAccommodationFilterDisabled =
      relevantPackageTypes?.some(
        (packageType) => packageType.disableAccommodationFilter === true
      ) === true;

    const updatedFilterOptions = { ...filterOptions };
    // include all matches as query if all matches are selected and a tournament is selected
    if (filterOptions.tournaments && (!matches || (matches && !matches.length))) {
      const allMatches = tournaments
        .filter((tournament) => filterOptions.tournaments.includes(tournament.id))
        .reduce((prev, cur) => [...prev, ...cur.matches.map(({ id }) => id)], []);
      updatedFilterOptions.matches = allMatches;
    }

    if (
      accommodationTypes &&
      !isAccommodationFilterDisabled &&
      (accommodationTypeIds?.length ?? 0) <= 0
    ) {
      updatedFilterOptions.accommodationTypeIds = accommodationTypes.map(
        (accommodation) => accommodation.id
      );
    }

    // Package types with disableAccommodationFilter (eg. Flight & Ticket) have no
    // accommodation option in the index, so forcing this filter into the request
    // would AND against a field that can never match and zero out the results.
    if (isAccommodationFilterDisabled) {
      delete updatedFilterOptions.accommodationTypeIds;
    }

    // include all Inspire Me destinations as query if Inspire Me is selected and
    // the visitor hasn't picked any destinations of their own yet - mirrors the
    // "all matches" default above. Must not run otherwise, or it overwrites the
    // visitor's actual destination selection with every configured destination.
    const { destinations: inspireMeDestinations } = inspireMe ?? {};
    const { items: inspireMeDestinationItems } = inspireMeDestinations ?? {};
    if (
      filterOptions.packageTypes === inspireMeDropdownValue &&
      !(filterOptions.destinations?.length > 0) &&
      inspireMeDestinationItems?.length > 0
    ) {
      const allDestinations = inspireMeDestinationItems.map((x) => x.id);
      updatedFilterOptions.destinations = allDestinations;
      filterOptions.destinations = allDestinations;
    }

    if (updatedFilterOptions.packageTypes === inspireMeDropdownValue) {
      updatedFilterOptions.packageTypes = '';
    }

    if (updatedFilterOptions.packageTypes === '') {
      delete updatedFilterOptions.packageTypes;
    }

    if (
      updatedFilterOptions.toDate === '' ||
      updatedFilterOptions.toDate === updatedFilterOptions.fromDate
    ) {
      delete updatedFilterOptions.toDate;
    }

    if (
      updatedFilterOptions.tourTypeSubFilters === null ||
      updatedFilterOptions.tourTypeSubFilters?.length === 0
    ) {
      delete updatedFilterOptions.tourTypeSubFilters;
    }

    // Search on the CMS price boundary unless the customer chose the range
    // themselves. Sending the displayed range instead let each search constrain
    // the next, so the range could only ever narrow and packages priced outside
    // it stayed hidden with no way back other than Reset.
    if (!this.priceFilterSetByUser) {
      updatedFilterOptions.priceFrom = filterOptions.minPrice;
      updatedFilterOptions.priceTo = filterOptions.maxPrice;
    }

    delete updatedFilterOptions.minPrice;
    delete updatedFilterOptions.maxPrice;

    const tourSearchApiRequestWithCulture =
      addCultureAndFallbackToParamsFromLocalStorage(updatedFilterOptions);
    const tourSearchApiRequestOptionsWithCultureAndCurrency = {
      ...tourSearchApiRequestWithCulture,
      currency: this.state.currentCurrency.code,
      includeAllMatchFilterItems: this.props.filters.enableMatchFilterAndOrSelection
        ? this.state.includeAllMatchFilterItems
        : false,
    };

    // Get JSON results
    jsonGet(this.props.apiURL, tourSearchApiRequestOptionsWithCultureAndCurrency, (data) => {
      let { items } = data;

      // Normalise null/undefined items to empty array
      if (!items || !Array.isArray(items)) {
        items = [];
      }

      // Check no new API requests have been made
      if (apiRequestID !== this.apiRequestID) return;

      // Optionally concat results array with existing results
      if (concatItemSets) {
        const currentItems = this.state.items || [];
        items = currentItems.concat(items);
      }

      // Update the tournamentID when a single tournament is selected.
      if (filterOptions.tournaments && filterOptions.tournaments.length === 1) {
        [tournamentID] = filterOptions.tournaments;
      }

      const { lastPage } = this.state;
      let nextPage = Number(filterOptions.page);
      const loadNextPage = Number(lastPage) && Number(lastPage) > nextPage;

      if (loadNextPage) {
        nextPage += 1;
        filterOptions.page = nextPage;
      }

      // Move the slider handles to the price range of the packages that came
      // back. The search ran on the CMS boundary, so this range can widen as
      // well as narrow. Skipped once the customer has set the range themselves,
      // so their selection is never overridden.
      if (!this.priceFilterSetByUser) {
        const { priceFrom, priceTo } = getPriceRangeFromSearchResults({
          minimumPricePerCurrency: data.minimumPricePerCurrency,
          maximumPricePerCurrency: data.maximumPricePerCurrency,
          currencyCode: this.state.currentCurrency.code,
          minPrice: filterOptions.minPrice,
          maxPrice: filterOptions.maxPrice,
        });

        filterOptions.priceFrom = priceFrom;
        filterOptions.priceTo = priceTo;
      }

      // Track the search in CRM now the results are in, so the price range
      // reported is the one the customer ends up seeing rather than the CMS
      // boundary the search was run on. Superseded searches return above, so
      // rapid filter changes no longer report a search the customer abandoned.
      this.postSearchToCrm(updatedFilterOptions, {
        priceFrom: filterOptions.priceFrom,
        priceTo: filterOptions.priceTo,
      });

      // Update state results and flags
      this.setState(
        {
          filterOptions,
          packageTypes: filterOptions.packageTypes,
          tourTypeSubFilters: filterOptions.tourTypeSubFilters,
          items,
          loadingPage: false,
          loadingResults: false,
          tournamentID,
          totalItemCount: data.totalItemCount,
        },
        () => {
          this.setQuery(filterOptions, pushHistory);
          if (!this.state.scrolled) {
            this.setState({ scrolled: true });
            const parsedQuery = qs.parse(window.location.search) || {};
            if (this.state.initialScroll && parsedQuery.page) {
              window.scrollTo(0, Number(this.state.initialScroll));
            }
          }

          // Load multiple pages if a page is passed from query
          if (loadNextPage) {
            this.loadResults(true);
          } else if (Number(lastPage) === Number(filterOptions.page)) {
            this.setState({
              lastPage: null,
            });
          }
          // Dispatch custom event to notify CompareListComponent of re-render
          window.dispatchEvent(new CustomEvent(CUSTOM_EVENT.GET_COMPARE_BUTTON_STATE_ON_REMOUNT));
        }
      );
    });
  };

  // Reports a completed search to CRM. `requestOptions` is the resolved search
  // request, so the filters reported are the ones actually searched on;
  // `displayedPriceRange` is the range left on the slider once the results came
  // back, which is what the customer sees.
  postSearchToCrm = (requestOptions, displayedPriceRange) => {
    const { filters } = this.props;

    const crmApiRequest = {
      siteKey: requestOptions.siteKey,
      fromDate: requestOptions.fromDate,
      toDate: requestOptions.toDate,
      priceFrom: displayedPriceRange.priceFrom,
      priceTo: displayedPriceRange.priceTo,
      currency: this.state.currentCurrency.code,
      tournaments: filters.tournaments
        ?.filter((x) => requestOptions.tournaments?.includes(x.id))
        .map((y) => y.title),
      matches: [
        ...new Set(
          filters.tournaments
            ?.flatMap((f) => f.matches)
            .filter((x) => requestOptions.matches?.includes(x.id))
            .map((y) => y.title)
        ),
      ],
      durations: filters.durations
        ?.filter((x) => requestOptions.durations?.includes(x.id))
        .map((y) => y.title),
      packageTypes: filters.packageTypes
        ?.filter((x) => requestOptions.packageTypes?.includes(x.id))
        .map((y) => y.title),
      accommodationTypeIds: filters.accommodationTypes
        ?.filter((x) => requestOptions.accommodationTypeIds?.includes(x.id))
        .map((y) => y.title),
      inclusions: filters.inclusions
        ?.filter((x) => requestOptions.inclusions?.includes(x.id))
        .map((y) => y.title),
      destinations: filters.inspireMe?.destinations?.items
        ?.filter((x) => requestOptions.destinations?.includes(x.id))
        .map((y) => y.title),
      tourTypeSubFilters: [
        ...new Set(
          filters?.packageTypes
            ?.flatMap((f) => f?.tourTypeSubFilter || [])
            .filter((x) => x && requestOptions?.tourTypeSubFilters?.includes(x.id))
            .map((y) => y?.title)
        ),
      ],
      holidayTypes: filters.inspireMe?.holidayTypes?.items
        ?.filter((x) => requestOptions.holidayTypes?.includes(x.id))
        .map((y) => y.title),
      attractions: filters.inspireMe?.attractions?.items
        ?.filter((x) => requestOptions.attractions?.includes(x.id))
        .map((y) => y.title),
      partySizes: filters.inspireMe?.partySizes?.items
        ?.filter((x) => requestOptions.partySizes?.includes(x.id))
        .map((y) => y.title),
      eventType: '', // TODO: Unclear but required, pass in empty string for now for BE
    };

    jsonPost(this.props.crmApiURL, crmApiRequest, (response) => {
      if (response.errorCode) console.error(response.errorMessage || response.message);
    });
  };

  render() {
    const useGridLayout = this.props.searchResultsLayout === searchResultsLayout.Grid;

    const tournamentDestinationOptions = this.props.filters.tournaments.find(
      (tournament) => tournament.id === this.state.tournamentID
    )?.destinations;

    const relevantPackageTypes = getRelevantPackageTypes(
      this.props.filters.packageTypes,
      this.state.filterOptions.packageTypes
    );
    const shouldHideAccommodationTypeFilter =
      this.props.hideAccommodationTypeFilter ||
      relevantPackageTypes?.some(
        (packageType) => packageType.disableAccommodationFilter === true
      ) === true;

    return (
      <section className="PackageSearch">
        <ErrorBoundary>
          <header className="PackageSearch_header">
            <div className="PackageSearch_featureCarousel">
              <FeatureCarousel
                autoplay={this.props.autoplay}
                fromPriceText={this.props.fromPriceText}
                items={this.props.featuredItems}
                interval={this.props.interval}
                limitedAvailabilityText={this.props.limitedAvailabilityText}
                perPersonText={this.props.perPersonText}
                sideButtons
                soldOutText={this.props.soldOutText}
                currentCurrency={this.state.currentCurrency}
                viewTourText={this.props.viewTourText}
              />
            </div>
          </header>
          {this.props.filters.tournaments &&
            this.props.filters.tournaments.length > 1 &&
            !this.props.hideFilters &&
            !this.props.hideTournamentFilter && (
              <PackageFilterContainer>
                <TournamentFilter
                  allOptionText={this.props.filters.allOptionText}
                  selectedTournaments={this.state.filterOptions.tournaments}
                  tournaments={this.props.filters.tournaments}
                  handleReset={this.handleReset}
                  handleValueChange={this.handleTournamentValueChange}
                  ref={this.tournamentFilterRef}
                />
              </PackageFilterContainer>
            )}
          {this.props.filters.packageTypes &&
            this.props.filters.packageTypes.length > 0 &&
            !this.props.hideFilters &&
            !this.props.hideTourTypeFilter && (
              <InspireMe
                allOptionText={this.props.filters.allOptionText}
                labels={this.props.labels}
                packageTypeSubFilterAllOptionText={
                  this.props.filters.packageTypeSubFilterAllOptionText
                }
                packageTypes={this.state.packageTypes}
                packages={this.props.filters.packageTypes}
                inspireMe={this.props.filters.inspireMe}
                destinations={this.state.filterOptions.destinations}
                partySizes={this.state.filterOptions.partySizes}
                attractions={this.state.filterOptions.attractions}
                holidayTypes={this.state.filterOptions.holidayTypes}
                tourTypeSubFilters={this.state.tourTypeSubFilters}
                handlePackageValueChange={this.handlePackageValueChange}
                handleValueChange={this.handleValueChange}
                tournamentDestinationOptions={tournamentDestinationOptions}
                hideInspireMeFilterGroup={this.props.hideInspireMeFilterGroup}
                hideTourSubTypeFilter={this.props.hideTourSubTypeFilter}
              />
            )}
          {!this.props.hideFilters && !this.props.hideInclusionFilter && (
            <Inclusion
              inclusions={this.props.filters.inclusions}
              selectedInclusions={this.state.filterOptions.inclusions}
              labelText={this.props.labels.inclusionFilterLabelText}
              onToggle={(id) => {
                const current = this.state.filterOptions.inclusions || [];
                const updated = current.includes(id)
                  ? current.filter((x) => x !== id)
                  : [...current, id];
                this.handleValueChange({ inclusions: updated });
              }}
            />
          )}
          {!this.props.hideFilters && (
            <PackageFilterContainer>
              <Filter
                currentCurrency={this.state.currentCurrency}
                filterOptions={this.state.filterOptions}
                filters={this.props.filters}
                labels={this.props.labels}
                handleReset={this.handleReset}
                handleValueChange={this.handleValueChange}
                resetButtonText={this.props.resetButtonText}
                tournamentID={this.state.tournamentID}
                hideDateRangeFilter={this.props.hideDateRangeFilter}
                hideDurationFilter={this.props.hideDurationFilter}
                hideDestinationFilter={this.props.hideDestinationFilter}
                hideMatchFilter={this.props.hideMatchFilter}
                hideAccommodationTypeFilter={shouldHideAccommodationTypeFilter}
                hidePriceRangeFilter={this.props.hidePriceRangeFilter}
                enableMatchFilterAndOrSelection={this.props.filters.enableMatchFilterAndOrSelection}
                includeAllMatchFilterItems={this.state.includeAllMatchFilterItems}
                matchFilterAndOrHelpText={this.props.labels.matchFilterAndOrHelpText}
                matchFilterAndOrInfoText={this.props.labels.matchFilterAndOrInfoText}
                handleMatchFilterAndOrChange={this.handleMatchFilterAndOrChange}
              />
            </PackageFilterContainer>
          )}
          {!this.props.hideFilters && (
            <PackageFilterContainer>
              <div className="PackageSearch_sortContainer">
                <Dropdown
                  blockName="PackageSearch_sort"
                  fieldName="sortOrder"
                  fieldValue={this.state.filterOptions.sortOrder}
                  handleChange={(e) => this.handleValueChange({ sortOrder: e.target.value })}
                  options={this.sortOptions}
                />
              </div>
            </PackageFilterContainer>
          )}
          {!this.props.hideSearchResults && (
            <div className="PackageSearch_results">
              <div
                className={
                  useGridLayout
                    ? 'PackageSearch_resultsContentGrid'
                    : 'PackageSearch_resultsContent'
                }
              >
                {this.state.loadingResults && <Loading />}
                {!this.state.loadingResults && this.state.items && (
                  <>
                    <Results
                      fromPriceText={this.props.fromPriceText}
                      items={this.state.items}
                      limitedAvailabilityText={this.props.limitedAvailabilityText}
                      includeAllMatchFilterItems={this.state.includeAllMatchFilterItems}
                      noResultsMessage={this.props.noResultsMessage}
                      soldOutText={this.props.soldOutText}
                      waitingListText={this.props.waitingListText}
                      useGridLayout={useGridLayout}
                      currentCurrency={this.state.currentCurrency}
                      viewTourText={this.props.viewTourText}
                      perPersonText={this.props.perPersonText}
                      widenSearchNoResultsMessage={this.props.widenSearchNoResultsMessage}
                    />
                    {this.state.items.length < this.state.totalItemCount && (
                      <div className="PackageSearch_pagination">
                        <Button
                          buttonType="button"
                          handleClick={this.handlePaginationClick}
                          isLoading={this.state.loadingPage}
                          modifier="ghost"
                          text={this.props.loadMoreButtonText}
                        />
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          )}
        </ErrorBoundary>
      </section>
    );
  }
}

PackageSearch.propTypes = propTypes;
PackageSearch.defaultProps = defaultProps;

export default PackageSearch;
